/** Shared custom print size presets + helpers (PrintOnly + framed canvas flows). */

export const CUSTOM_SIZE_OPTION = '__custom_wh__';

export const customSizeOptions = [
  { label: '6X4', value: '6x4' },
  { label: '5x7', value: '5x7' },
  { label: '6x8', value: '6x8' },
  { label: '8x10', value: '8x10' },
  { label: '8x12', value: '8x12' },
  { label: '10x12', value: '10x12' },
  { label: '11x14', value: '11x14' },
  { label: '12x16', value: '12x16' },
  { label: '12x18', value: '12x18' },
  { label: '16x20', value: '16x20' },
  { label: '16x24', value: '16x24' },
  { label: '20x24', value: '20x24' },
  { label: '20x30', value: '20x30' },
  { label: '24x30', value: '24x30' },
  { label: '24x36', value: '24x36' },
  { label: '30x40', value: '30x40' },
  { label: '40x60', value: '40x60' }
];

export const PRICE_TABLE = {
  '6x4': { standard: 1.0, entrada: 3.0 },
  '5x7': { standard: 3.95, entrada: 8.0 },
  '6x8': { standard: 5.95, entrada: 10.0 },
  '8x10': { standard: 12.95, entrada: 25.0 },
  '8x12': { standard: 14.95, entrada: 30.0 },
  '10x12': { standard: 18.95, entrada: 35.0 },
  '11x14': { standard: 19.95, entrada: 40.0 },
  '12x16': { standard: 24.95, entrada: 50.0 },
  '12x18': { standard: 24.95, entrada: 50.0 },
  '16x20': { standard: 39.95, entrada: 80.0 },
  '16x24': { standard: 39.95, entrada: 80.0 },
  '20x24': { standard: 59.95, entrada: 120.0 },
  '20x30': { standard: 69.95, entrada: 140.0 },
  '24x30': { standard: 75.0, entrada: 150.0 },
  '24x36': { standard: 85.0, entrada: 170.0 },
  '30x40': { standard: 140.0, entrada: 280.0 },
  '40x60': { standard: 230.0, entrada: 460.0 }
};

const SIZE_ORDER = [
  [6, 4],
  [5, 7],
  [6, 8],
  [8, 10],
  [8, 12],
  [10, 12],
  [11, 14],
  [12, 16],
  [12, 18],
  [16, 20],
  [16, 24],
  [20, 24],
  [20, 30],
  [24, 30],
  [24, 36],
  [30, 40],
  [40, 60]
];

export function parseCustomDims(customWidthIn, customHeightIn) {
  const aw = Number(customWidthIn);
  const ah = Number(customHeightIn);
  if (!Number.isFinite(aw) || !Number.isFinite(ah) || aw <= 0 || ah <= 0) {
    return null;
  }
  return { w: aw, h: ah };
}

export function getImageSizeInches(imageSizeCm) {
  const widthCm = Number(imageSizeCm?.widthCm || 0);
  const heightCm = Number(imageSizeCm?.heightCm || 0);
  const widthIn = Math.round(widthCm / 2.54);
  const heightIn = Math.round(heightCm / 2.54);
  return { widthIn, heightIn, normalizedSize: `${widthIn}x${heightIn}` };
}

export function getPrintAreaInches(customSize, customDims, widthIn, heightIn) {
  if (customSize === CUSTOM_SIZE_OPTION && customDims) {
    return { w: customDims.w, h: customDims.h };
  }
  if (customSize && customSize !== CUSTOM_SIZE_OPTION) {
    const m = /^(\d+)x(\d+)$/.exec(customSize);
    if (m) {
      return { w: Number(m[1]), h: Number(m[2]) };
    }
  }
  return { w: widthIn, h: heightIn };
}

/** Opening size in cm (numbers) from custom selection or image. */
export function getEffectiveOpeningCm(customSize, customDims, imageSizeCm) {
  const { widthIn, heightIn } = getImageSizeInches(imageSizeCm);
  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);
  return {
    widthCm: area.w * 2.54,
    heightCm: area.h * 2.54
  };
}

export function priceForNearestTier(printType, longIn, shortIn) {
  let w = Math.max(longIn, shortIn);
  let h = Math.min(longIn, shortIn);

  if (w <= 6 && h <= 4) {
    const key = '6x4';
    const sizeData = PRICE_TABLE[key];
    return {
      key,
      price: printType === 'entrada' ? sizeData.entrada : sizeData.standard
    };
  }

  if (w >= 40 || h >= 60) {
    const key = '40x60';
    const sizeData = PRICE_TABLE[key];
    return {
      key,
      price: printType === 'entrada' ? sizeData.entrada : sizeData.standard
    };
  }

  let selected = SIZE_ORDER[SIZE_ORDER.length - 1];
  for (const [sw, sh] of SIZE_ORDER) {
    if (w <= sw && h <= sh) {
      selected = [sw, sh];
      break;
    }
  }

  const key = `${selected[0]}x${selected[1]}`;
  const sizeData = PRICE_TABLE[key];
  return {
    key,
    price: printType === 'entrada' ? sizeData.entrada : sizeData.standard
  };
}

export function nearestTierForPricing({
  printType,
  customSize,
  customDims,
  widthIn,
  heightIn
}) {
  if (customSize && customSize !== CUSTOM_SIZE_OPTION) {
    const selectedSizeData = PRICE_TABLE[customSize];
    if (selectedSizeData) {
      return {
        key: customSize,
        price:
          printType === 'entrada'
            ? selectedSizeData.entrada
            : selectedSizeData.standard
      };
    }
  }
  if (customSize === CUSTOM_SIZE_OPTION && customDims) {
    return priceForNearestTier(printType, customDims.w, customDims.h);
  }
  return priceForNearestTier(printType, widthIn, heightIn);
}

export function getCustomSizeMetaString(normalizedSize, customSize, customDims) {
  if (customSize === CUSTOM_SIZE_OPTION && customDims) {

    // convert inches back to cm for display/meta
    const widthCm = (customDims.w * 2.54).toFixed(1);
    const heightCm = (customDims.h * 2.54).toFixed(1);

    return `${widthCm}x${heightCm} cm`;
  }

  if (customSize && customSize !== CUSTOM_SIZE_OPTION) {
    return customSize;
  }

  return normalizedSize;
}

export function computePreviewCanvasSize(
  area,
  maxPreviewW = 680,
  maxPreviewH = 460
) {
  const awIn = Math.max(Number(area.w) || 1, 0.01);
  const ahIn = Math.max(Number(area.h) || 1, 0.01);
  const previewAspect = awIn / ahIn;
  let previewCanvasW;
  let previewCanvasH;
  if (previewAspect >= maxPreviewW / maxPreviewH) {
    previewCanvasW = maxPreviewW;
    previewCanvasH = Math.max(1, Math.round(maxPreviewW / previewAspect));
  } else {
    previewCanvasH = maxPreviewH;
    previewCanvasW = Math.max(1, Math.round(maxPreviewH * previewAspect));
  }
  return { previewCanvasW, previewCanvasH };
}
